// src/screens/Dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  limit,
  updateDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import {
  LayoutDashboard,
  RefreshCcw,
  Building2,
  ClipboardList,
  Package,
  BadgePercent,
  Sparkles,
  Flame,
  PlusCircle,
  DollarSign,
  MapPinned,
  UserCheck,
  Clock3,
  ArrowRight,
  Boxes,
  Users,
  ImagePlus,
  CheckSquare,
} from "lucide-react";

import ActivityLog from "../components/dashboard/ActivityLog";
import TasksSummary from "../components/dashboard/TasksSummary";
import SampleOrderCostTimeFrame from "../components/metrics/SampleOrderCostTimeFrame";
import RouteBuilderModal from "../components/routes/RouteBuilderModal";
import { TaskService } from "../components/tasks/TaskService";
import SurfaceState from "../components/common/SurfaceState";

const normalizeRole = (role) => String(role || "").trim().toLowerCase();

function safeDateSeconds(ts) {
  return ts?.seconds || 0;
}

function toNumber(v) {
  const n =
    typeof v === "number"
      ? v
      : parseFloat(String(v || "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function normalizeStatus(s) {
  return String(s || "").trim().toLowerCase();
}

function getAssignedInternalUserId(record = {}) {
  return (
    record?.assignedToId ||
    record?.assignedUserId ||
    record?.repUserId ||
    record?.salesRepId ||
    null
  );
}

function getAssignedInternalUserIds(record = {}) {
  return Array.from(
    new Set(
      [
        getAssignedInternalUserId(record),
        ...(Array.isArray(record?.assignedToUserIds) ? record.assignedToUserIds : []),
        ...(Array.isArray(record?.assignedUserIds) ? record.assignedUserIds : []),
        ...(Array.isArray(record?.assignedTo) ? record.assignedTo : []),
        ...(Array.isArray(record?.userIds) ? record.userIds : []),
        ...(Array.isArray(record?.memberIds) ? record.memberIds : []),
      ]
        .filter(Boolean)
        .map((value) => String(value))
    )
  );
}

function isRecordAssignedToUser(record = {}, userId) {
  if (!userId) return false;
  return getAssignedInternalUserIds(record).includes(String(userId));
}

function getTerritoryId(record = {}) {
  return (
    record?.territoryId ||
    record?.territory?.id ||
    record?.territory?.territoryId ||
    record?.territoryID ||
    null
  );
}

function getUserTerritoryIds(user = {}) {
  return Array.from(
    new Set(
      [
        ...(Array.isArray(user?.territoryIds) ? user.territoryIds : []),
        ...(Array.isArray(user?.territories) ? user.territories : []),
      ]
        .filter(Boolean)
        .map((value) => String(value))
    )
  );
}

function isConvertedOrder(o) {
  if (o?.convertedToOrder === true || o?.isConverted === true) return true;
  const st = normalizeStatus(o?.status);
  if (!st) return false;
  return st.includes("converted") || st.includes("ordered");
}

function getInventoryTitle(it) {
  return it?.name || it?.title || it?.itemName || "Unnamed item";
}

function getInventoryCreatedTs(it) {
  return it?.createdAt || it?.created || it?.addedAt || it?.createdOn || null;
}

function formatPct(n) {
  const v = Math.round((Number.isFinite(n) ? n : 0) * 100);
  return `${v}%`;
}

function StatPill({ icon: Icon, label, value, color = "gray" }) {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    gray: "bg-gray-50 border-gray-200 text-gray-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
  };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border ${colorClasses[color]} shadow-sm`}>
      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="leading-tight">
        <div className="text-[11px] font-medium">{label}</div>
        <div className="text-sm font-bold">{value}</div>
      </div>
    </div>
  );
}

function DashboardCard({ title, subtitle, right, children }) {
  return (
    <div className="bg-white/95 backdrop-blur border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            {subtitle ? (
              <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
            ) : null}
          </div>
          {right ? <div className="flex-shrink-0">{right}</div> : null}
        </div>
      </div>
      <div className="p-3.5">{children}</div>
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="text-sm text-gray-500">{children}</div>;
}

function MobileMiniMetric({ icon: Icon, label, value, detail, tone = "slate", onClick }) {
  const tones = {
    slate: "border-slate-200 bg-white",
    blue: "border-blue-200 bg-blue-50/80",
    green: "border-emerald-200 bg-emerald-50/80",
    amber: "border-amber-200 bg-amber-50/80",
    purple: "border-purple-200 bg-purple-50/80",
  };

  const content = (
    <div className={`rounded-xl border px-3 py-2.5 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        {Icon ? (
          <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/80 bg-white">
            <Icon className="h-3.5 w-3.5 text-slate-700" />
          </div>
        ) : null}
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          <div className="text-sm font-bold text-slate-900">{value}</div>
          {detail ? <div className="truncate text-[11px] text-slate-500">{detail}</div> : null}
        </div>
      </div>
    </div>
  );

  if (!onClick) return content;
  return (
    <button type="button" onClick={onClick} className="w-full text-left">
      {content}
    </button>
  );
}

function MobileActionRow({ title, subtitle, badge, onClick, actionLabel = "Open" }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <button type="button" onClick={onClick} className="min-w-0 flex-1 text-left">
        <div className="truncate text-sm font-semibold text-slate-900">{title}</div>
        {subtitle ? <div className="mt-0.5 truncate text-[11px] text-slate-500">{subtitle}</div> : null}
      </button>
      {badge ? (
        <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-600">
          {badge}
        </span>
      ) : null}
      <button
        type="button"
        onClick={onClick}
        className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
      >
        {actionLabel}
      </button>
    </div>
  );
}

function MetricTile({
  icon: Icon,
  label,
  value,
  detail,
  tone = "slate",
  onClick,
}) {
  const tones = {
    slate: "border-slate-200 bg-white",
    blue: "border-blue-200 bg-blue-50/70",
    green: "border-emerald-200 bg-emerald-50/70",
    amber: "border-amber-200 bg-amber-50/70",
    purple: "border-purple-200 bg-purple-50/70",
  };

  const content = (
    <div className={`rounded-2xl border ${tones[tone] || tones.slate} p-3`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div className="mt-1 text-xl md:text-2xl font-bold text-slate-900">{value}</div>
          {detail ? <div className="mt-1 text-xs text-slate-500">{detail}</div> : null}
        </div>
        {Icon ? (
          <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-slate-700" />
          </div>
        ) : null}
      </div>
    </div>
  );

  if (!onClick) return content;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left transition-transform hover:-translate-y-0.5"
    >
      {content}
    </button>
  );
}

function ActionLink({ label, onClick, icon: Icon, primary = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
        primary
          ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : null}
      {label}
    </button>
  );
}

function QuickRow({ title, subtitle, badge, onClick, accent = "slate" }) {
  const accents = {
    slate: "border-slate-200 hover:border-slate-300",
    blue: "border-blue-200 hover:border-blue-300",
    purple: "border-purple-200 hover:border-purple-300",
    green: "border-emerald-200 hover:border-emerald-300",
    amber: "border-amber-200 hover:border-amber-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border px-3 py-2.5 bg-white hover:bg-slate-50 transition-colors ${accents[accent] || accents.slate}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
          {subtitle ? <div className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {badge ? (
            <span className="text-[11px] px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-700 font-semibold">
              {badge}
            </span>
          ) : null}
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    </button>
  );
}

function QuickStartTile({ icon: Icon, title, description, onClick, tone = "slate" }) {
  const tones = {
    slate: "bg-white/80 text-slate-700 hover:bg-white",
    blue: "bg-blue-50 text-blue-700 hover:bg-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    amber: "bg-amber-50 text-amber-700 hover:bg-amber-100",
    purple: "bg-purple-50 text-purple-700 hover:bg-purple-100",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-2xl px-3 py-3 text-left transition-colors ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-2xl bg-white shadow-sm">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="mt-1 text-xs text-slate-500">{description}</div>
        </div>
      </div>
    </button>
  );
}

function extractSampleRefsFromOrder(o) {
  const arrays = [
    o?.items,
    o?.samples,
    o?.sampleItems,
    o?.sampleSelections,
    o?.selectedSamples,
    o?.lineItems,
  ].filter(Array.isArray);

  if (arrays.length > 0) {
    const arr = arrays[0];
    return arr
      .map((x) => {
        const id = x?.inventoryId || x?.sampleId || x?.itemId || x?.id || null;
        const name = x?.name || x?.title || x?.itemName || null;
        const qty = toNumber(x?.quantity ?? x?.qty ?? x?.count ?? 1) || 1;
        return { id, name, qty };
      })
      .filter((x) => x.id || x.name);
  }

  const idArrays = [o?.sampleIds, o?.inventoryIds, o?.itemIds].filter(
    Array.isArray
  );
  if (idArrays.length > 0) {
    return idArrays[0]
      .map((id) => ({ id: String(id || ""), name: null, qty: 1 }))
      .filter((x) => x.id);
  }

  return [];
}

function getAccountName(account = {}) {
  return account?.name || account?.accountName || account?.businessName || "Unnamed account";
}

function isActiveSampleOrder(order = {}) {
  const status = normalizeStatus(order?.status);
  if (!status) return true;
  if (status.includes("cancel")) return false;
  if (status.includes("complete")) return false;
  if (status.includes("convert")) return false;
  if (status.includes("order")) return false;
  return true;
}

function getUserGroupNames(user = {}, groups = []) {
  const groupMap = new Map();
  (groups || []).forEach((group) => {
    if (!group) return;
    const id = group?.id || group?.groupId;
    if (!id) return;
    groupMap.set(String(id), group);
  });

  return Array.from(
    new Set(
      (Array.isArray(user?.groupIds) ? user.groupIds : [])
        .map((groupId) => groupMap.get(String(groupId)))
        .map((group) => group?.name || group?.title || group?.label || null)
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
}

export default function DashboardScreen({
  companyId: companyIdProp = null,
  me: meProp = null,
  firebaseUser: firebaseUserProp = null,
}) {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(firebaseUserProp || auth.currentUser || null);
  const [userProfile, setUserProfile] = useState(meProp || null);
  const [companyId, setCompanyId] = useState(companyIdProp || meProp?.companyId || null);

  const [accounts, setAccounts] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [groups, setGroups] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [teamMemberCount, setTeamMemberCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [approvingOrderId, setApprovingOrderId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState("");

  const [showRouteModal, setShowRouteModal] = useState(false);
  const hasResolvedAppContext = Boolean(meProp && (companyIdProp || meProp?.companyId));

  const handleNavigateToAccount = (accountId) => {
    if (!accountId) return;
    navigate(`/accounts?edit=${encodeURIComponent(accountId)}`);
  };

  const handleNavigateToInventory = (itemId = null) => {
    const target = itemId ? `/inventory?itemId=${encodeURIComponent(itemId)}` : "/inventory";
    navigate(target);
  };

  const handleNavigateToSampleOrders = (requestId = null) => {
    const params = requestId ? `?requestId=${encodeURIComponent(requestId)}&view=create` : "";
    navigate(`/sample-orders${params}`);
  };

  const handleNavigateToAccounts = () => {
    navigate("/accounts");
  };

  const handleNavigateToTasks = () => {
    navigate("/tasks");
  };

  const handleNavigateToTeam = () => {
    navigate("/team");
  };

  const handleNavigateToTerritories = () => {
    navigate("/territories-map");
  };

  const handleNavigateToProfile = () => {
    navigate("/profile");
  };

  const handleCreateSampleOrder = () => {
    navigate("/sample-orders?view=create");
  };

  const handleQuickApprove = async (orderId) => {
    if (!companyId || !orderId || !isAdminUser) return;
    setApprovingOrderId(orderId);
    try {
      await updateDoc(doc(db, "companies", companyId, "sampleOrders", orderId), {
        status: "Approved",
        updatedAt: serverTimestamp(),
        statusUpdatedAt: serverTimestamp(),
        statusUpdatedBy: firebaseUser?.uid || null,
      });
      setSampleOrders((prev) =>
        (prev || []).map((order) =>
          order?.id === orderId
            ? { ...order, status: "Approved", statusUpdatedBy: firebaseUser?.uid || null }
            : order
        )
      );
    } catch (e) {
      console.error("[Dashboard] quick approve failed:", e);
      setError(e?.message || "Could not approve the sample order.");
    } finally {
      setApprovingOrderId(null);
    }
  };

  // ----------------------------
  // Loaders
  // ----------------------------
  const loadAccounts = async ({ compId }) => {
    try {
      const colRef = collection(db, "companies", compId, "accounts");
      const snap = await getDocs(colRef);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setAccounts(list);
      return list;
    } catch (e) {
      console.error("[Dashboard] loadAccounts failed:", e);
      setAccounts([]);
      return [];
    }
  };

  const loadSampleOrders = async ({ compId }) => {
    try {
      const colRef = collection(db, "companies", compId, "sampleOrders");
      const snap = await getDocs(colRef);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => safeDateSeconds(b.createdAt) - safeDateSeconds(a.createdAt));
      setSampleOrders(list);
      return list;
    } catch (e) {
      console.error("[Dashboard] loadSampleOrders failed:", e);
      setSampleOrders([]);
      return [];
    }
  };

  const loadInventory = async ({ compId }) => {
    try {
      const colRef = collection(db, "companies", compId, "inventory");
      const snap = await getDocs(query(colRef, limit(400)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setInventory(list);
      return list;
    } catch (e) {
      console.error("[Dashboard] loadInventory failed:", e);
      setInventory([]);
      return [];
    }
  };

  const loadGroups = async ({ compId }) => {
    try {
      const colRef = collection(db, "companies", compId, "groups");
      const snap = await getDocs(query(colRef, limit(300)));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );
      setGroups(list);
      return list;
    } catch (e) {
      console.error("[Dashboard] loadGroups failed:", e);
      setGroups([]);
      return [];
    }
  };

  const loadTerritories = async ({ compId }) => {
    try {
      const colRef = collection(db, "companies", compId, "territories");
      const snap = await getDocs(query(colRef, limit(300)));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );
      setTerritories(list);
      return list;
    } catch (e) {
      console.error("[Dashboard] loadTerritories failed:", e);
      setTerritories([]);
      return [];
    }
  };

  const loadCompanyMeta = async ({ compId, profile }) => {
    try {
      const currentProfile = profile || userProfile || {};
      const role = normalizeRole(currentProfile?.role);
      const isAdmin = role === "owner" || role === "admin";

      const companyPromise = getDoc(doc(db, "companies", compId));
      const teamPromise = isAdmin
        ? getDocs(query(collection(db, "users"), where("companyId", "==", compId), limit(25)))
        : Promise.resolve(null);
      const taskPromise = TaskService.getCompanyTasks(compId, { me: currentProfile });

      const [companySnap, teamSnap, taskRows] = await Promise.all([
        companyPromise,
        teamPromise,
        taskPromise,
      ]);

      const safeTaskCount = Array.isArray(taskRows) ? taskRows.length : 0;
      const safeTeamCount = isAdmin ? teamSnap?.size || 0 : 0;

      setCompanyData(companySnap.exists() ? { id: companySnap.id, ...companySnap.data() } : null);
      setTeamMemberCount(safeTeamCount);
      setTaskCount(safeTaskCount);
      return {
        company: companySnap.exists() ? { id: companySnap.id, ...companySnap.data() } : null,
        teamMemberCount: safeTeamCount,
        taskCount: safeTaskCount,
      };
    } catch (e) {
      console.error("[Dashboard] loadCompanyMeta failed:", e);
      setCompanyData(null);
      setTeamMemberCount(0);
      setTaskCount(0);
      return { company: null, teamMemberCount: 0, taskCount: 0 };
    }
  };

  const loadAll = async ({ compId, profile }) => {
    const results = await Promise.allSettled([
      loadAccounts({ compId }),
      loadSampleOrders({ compId }),
      loadInventory({ compId }),
      loadGroups({ compId }),
      loadTerritories({ compId }),
      loadCompanyMeta({ compId, profile }),
    ]);

    const rejected = results.find((r) => r.status === "rejected");
    if (rejected) {
      console.error("[Dashboard] loadAll partial failure:", rejected.reason);
      setError(
        rejected.reason?.message || "Some dashboard sections failed to load."
      );
    }
  };

  // ----------------------------
  // Auth bootstrap
  // ----------------------------
  useEffect(() => {
    if (!hasResolvedAppContext) return undefined;

    const resolvedUser = firebaseUserProp || auth.currentUser || null;
    const resolvedProfile = meProp || null;
    const resolvedCompanyId = companyIdProp || meProp?.companyId || null;
    let cancelled = false;

    setFirebaseUser(resolvedUser);
    setUserProfile(resolvedProfile);
    setCompanyId(resolvedCompanyId);
    setError("");

    if (!resolvedUser || !resolvedCompanyId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    loadAll({ compId: resolvedCompanyId, profile: resolvedProfile })
      .catch((e) => {
        console.error("Dashboard load failed:", e);
        if (!cancelled) {
          setError(e?.message || "Failed to load dashboard.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResolvedAppContext, firebaseUserProp, meProp, companyIdProp]);

  useEffect(() => {
    if (hasResolvedAppContext) return undefined;

    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setError("");

      if (!user) {
        setFirebaseUser(null);
        setUserProfile(null);
        setCompanyId(null);
        setAccounts([]);
        setSampleOrders([]);
        setInventory([]);
        setGroups([]);
        setTerritories([]);
        setCompanyData(null);
        setTeamMemberCount(0);
        setTaskCount(0);
        setLoading(false);
        return;
      }

      setFirebaseUser(user);

      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (!userSnap.exists()) {
          setError("User profile not found.");
          setLoading(false);
          return;
        }

        const profile = {
          id: userSnap.id,
          uid: user.uid,
          ...userSnap.data(),
        };
        setUserProfile(profile);

        const compId = profile?.companyId || null;
        if (!compId) {
          setError("Your user profile is missing companyId.");
          setCompanyId(null);
          setLoading(false);
          return;
        }

        setCompanyId(compId);
        await loadAll({ compId, profile });
      } catch (e) {
        console.error("Dashboard load failed:", e);
        setError(e?.message || "Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResolvedAppContext]);

  const handleRefresh = async () => {
    if (!companyId) return;
    setReloading(true);
    setError("");
    try {
      await loadAll({ compId: companyId, profile: userProfile });
    } catch (e) {
      console.error("Refresh failed:", e);
      setError(e?.message || "Refresh failed.");
    } finally {
      setReloading(false);
    }
  };

  // ----------------------------
  // Derived Data
  // ----------------------------
  const accountById = useMemo(() => {
    const map = new Map();
    (accounts || []).forEach((a) => map.set(a.id, a));
    return map;
  }, [accounts]);

  const roleLower = useMemo(() => normalizeRole(userProfile?.role), [userProfile?.role]);
  const isAdminUser = roleLower === "owner" || roleLower === "admin";
  const myTerritoryIds = useMemo(() => getUserTerritoryIds(userProfile), [userProfile]);
  const myTerritoryIdSet = useMemo(() => new Set(myTerritoryIds), [myTerritoryIds]);

  const visibleAccounts = useMemo(() => {
    if (isAdminUser) return accounts || [];

    const me = firebaseUser?.uid || null;
    return (accounts || []).filter((account) => {
      const territoryId = getTerritoryId(account);
      const createdBy = account?.createdBy || null;

      return (
        isRecordAssignedToUser(account, me) ||
        (me && createdBy === me) ||
        (territoryId && myTerritoryIdSet.has(String(territoryId)))
      );
    });
  }, [accounts, firebaseUser?.uid, isAdminUser, myTerritoryIdSet]);

  const visibleAccountIds = useMemo(() => {
    return new Set((visibleAccounts || []).map((account) => String(account.id)));
  }, [visibleAccounts]);

  const visibleSampleOrders = useMemo(() => {
    if (isAdminUser) return sampleOrders || [];

    const me = firebaseUser?.uid || null;
    return (sampleOrders || []).filter((order) => {
      const accountId = order?.accountId ? String(order.accountId) : null;
      const createdBy = order?.createdBy || null;
      const orderTerritoryId = getTerritoryId(order);
      const accountTerritoryId =
        accountId && accountById.has(accountId)
          ? getTerritoryId(accountById.get(accountId))
          : null;

      return (
        isRecordAssignedToUser(order, me) ||
        (me && createdBy === me) ||
        (accountId && visibleAccountIds.has(accountId)) ||
        (orderTerritoryId && myTerritoryIdSet.has(String(orderTerritoryId))) ||
        (accountTerritoryId && myTerritoryIdSet.has(String(accountTerritoryId)))
      );
    });
  }, [
    accountById,
    firebaseUser?.uid,
    isAdminUser,
    myTerritoryIdSet,
    sampleOrders,
    visibleAccountIds,
  ]);

  const visibleAssignedAccounts = useMemo(() => {
    const me = firebaseUser?.uid || null;
    if (!me) return [];
    return (visibleAccounts || []).filter(
      (account) => isRecordAssignedToUser(account, me)
    );
  }, [visibleAccounts, firebaseUser?.uid]);

  const visibleTerritories = useMemo(() => {
    if (isAdminUser) return territories || [];
    return (territories || []).filter((territory) =>
      myTerritoryIdSet.has(String(territory.id))
    );
  }, [isAdminUser, myTerritoryIdSet, territories]);

  const pendingVisibleSamples = useMemo(() => {
    return (visibleSampleOrders || []).filter(
      (order) => normalizeStatus(order?.status) === "pending"
    );
  }, [visibleSampleOrders]);

  const followUpVisibleSamples = useMemo(() => {
    return (visibleSampleOrders || []).filter(
      (order) => normalizeStatus(order?.status) === "follow up"
    );
  }, [visibleSampleOrders]);

  const recentVisibleSamples = useMemo(() => {
    return (visibleSampleOrders || []).slice(0, 5);
  }, [visibleSampleOrders]);

  const inventoryById = useMemo(() => {
    const map = new Map();
    (inventory || []).forEach((it) => map.set(it.id, it));
    return map;
  }, [inventory]);

  const recentOrders = useMemo(() => {
    return recentVisibleSamples;
  }, [recentVisibleSamples]);

  const requestCounts = useMemo(() => {
    const counts = new Map();
    (visibleSampleOrders || []).forEach((o) => {
      const refs = extractSampleRefsFromOrder(o);
      refs.forEach((r) => {
        const key = r.id
          ? `id:${r.id}`
          : `name:${String(r.name || "").toLowerCase()}`;
        counts.set(key, (counts.get(key) || 0) + (r.qty || 1));
      });
    });
    return counts;
  }, [visibleSampleOrders]);

  const mostRequested = useMemo(() => {
    const items = [];

    for (const [key, qty] of requestCounts.entries()) {
      if (!qty) continue;

      let it = null;
      if (key.startsWith("id:")) {
        const id = key.slice(3);
        it = inventoryById.get(id) || null;
      } else if (key.startsWith("name:")) {
        const nm = key.slice(5);
        it =
          (inventory || []).find(
            (x) => String(getInventoryTitle(x)).trim().toLowerCase() === nm
          ) || null;
      }

      if (!it) continue;
      items.push({ item: it, requests: qty });
    }

    const bestById = new Map();
    items.forEach((row) => {
      const id = row.item?.id;
      if (!id) return;
      const cur = bestById.get(id);
      if (!cur || row.requests > cur.requests) bestById.set(id, row);
    });

    return [...bestById.values()]
      .sort((a, b) => b.requests - a.requests)
      .slice(0, 3);
  }, [inventory, inventoryById, requestCounts]);

  const featuredInventory = useMemo(() => {
    const list = Array.isArray(inventory) ? inventory : [];
    return list
      .filter((it) => it?.featured === true || it?.isFeatured === true)
      .sort((a, b) => {
        return String(getInventoryTitle(a)).localeCompare(
          String(getInventoryTitle(b))
        );
      })
      .slice(0, 3);
  }, [inventory]);

  const newInventory = useMemo(() => {
    const list = Array.isArray(inventory) ? inventory : [];
    return [...list]
      .sort(
        (a, b) =>
          safeDateSeconds(getInventoryCreatedTs(b)) -
          safeDateSeconds(getInventoryCreatedTs(a))
      )
      .slice(0, 3);
  }, [inventory]);

  const convertedVisibleSamples = useMemo(() => {
    return (visibleSampleOrders || []).filter((order) => isConvertedOrder(order));
  }, [visibleSampleOrders]);

  const lowStockVisibleInventory = useMemo(() => {
    return (inventory || [])
      .filter((item) => {
        const stock = Number(item?.stock ?? item?.quantity ?? 0);
        const threshold = Number(item?.lowStockThreshold ?? 10);
        return stock <= threshold;
      })
      .sort((a, b) => {
        const aStock = Number(a?.stock ?? a?.quantity ?? 0);
        const bStock = Number(b?.stock ?? b?.quantity ?? 0);
        return aStock - bStock;
      })
      .slice(0, 4);
  }, [inventory]);

  const myGroupNames = useMemo(() => {
    return getUserGroupNames(userProfile, groups).slice(0, 6);
  }, [groups, userProfile]);

  const myTerritoryNames = useMemo(() => {
    return (visibleTerritories || []).map((territory) => territory?.name || "Untitled territory").filter(Boolean).slice(0, 6);
  }, [visibleTerritories]);

  const quickInventoryLinks = useMemo(() => {
    if (mostRequested.length > 0) {
      return mostRequested.slice(0, 4).map(({ item, requests }) => ({
        item,
        badge: `${requests} req`,
        subtitle: item?.size ? `${item.size} ${item?.unit || ""}`.trim() : "Most requested item",
        accent: "green",
      }));
    }

    const fallback = featuredInventory.length > 0 ? featuredInventory : newInventory;
    return fallback.slice(0, 4).map((item) => ({
      item,
      badge: item?.featured === true || item?.isFeatured === true ? "Featured" : "New",
      subtitle: item?.size ? `${item.size} ${item?.unit || ""}`.trim() : "Inventory item",
      accent: "amber",
    }));
  }, [featuredInventory, mostRequested, newInventory]);

  const recentOrderHighlights = useMemo(() => {
    return (recentOrders || []).map((order) => {
      const account = order?.accountId ? accountById.get(order.accountId) : null;
      const sampleRef = extractSampleRefsFromOrder(order)?.[0] || null;
      const linkedItem =
        sampleRef?.id && inventoryById.has(String(sampleRef.id))
          ? inventoryById.get(String(sampleRef.id))
          : null;

      return {
        ...order,
        accountName: getAccountName(account || order),
        detail:
          linkedItem?.name ||
          sampleRef?.name ||
          order?.assignedToName ||
          "Ready to review",
      };
    });
  }, [accountById, inventoryById, recentOrders]);

  const mobileSampleQueue = useMemo(() => {
    const source = isAdminUser ? pendingVisibleSamples : followUpVisibleSamples;
    return source.slice(0, 5).map((order) => ({
      ...order,
      accountName: getAccountName(accountById.get(order?.accountId) || order),
      detail:
        order?.productName ||
        order?.itemName ||
        order?.assignedToName ||
        (isAdminUser ? "Waiting for review" : "Ready for follow-up"),
    }));
  }, [accountById, followUpVisibleSamples, isAdminUser, pendingVisibleSamples]);

  const mobileInventorySpotlight = useMemo(() => {
    const requested = (mostRequested || []).slice(0, 2).map(({ item, requests }) => ({
      id: item?.id,
      title: getInventoryTitle(item),
      detail: item?.size ? `${item.size} ${item?.unit || ""}`.trim() : "Most requested item",
      badge: `${requests} req`,
      tone: "green",
    }));

    const quick = (quickInventoryLinks || []).slice(0, 2).map(({ item, subtitle, badge, accent }) => ({
      id: item?.id,
      title: getInventoryTitle(item),
      detail: subtitle,
      badge,
      tone: accent === "amber" ? "amber" : accent === "green" ? "green" : "blue",
    }));

    return [...requested, ...quick].filter((row) => row.id).slice(0, 4);
  }, [mostRequested, quickInventoryLinks]);

  const quickStartActions = [];
  const hasLogo = Boolean(companyData?.logoUrl || companyData?.logoURL || companyData?.logo);
  const hasAccounts = (accounts || []).length > 0;
  const hasSamples = (sampleOrders || []).length > 0;
  const hasInventory = (inventory || []).length > 0;

  if (!hasInventory) {
    quickStartActions.push({ id: "inventory", icon: Package, title: "Add Inventory", description: "Open inventory and add your first products.", onClick: () => handleNavigateToInventory(), tone: "blue" });
  }
  if (!hasAccounts && !hasSamples) {
    quickStartActions.push({ id: "accounts", icon: Building2, title: "Add Accounts", description: "Go to accounts and start your customer list.", onClick: handleNavigateToAccounts, tone: "emerald" });
  }
  if (!taskCount) {
    quickStartActions.push({ id: "tasks", icon: CheckSquare, title: "Create your first Task", description: "Jump into tasks and create the first follow-up.", onClick: handleNavigateToTasks, tone: "amber" });
  }
  if (!hasSamples) {
    quickStartActions.push({ id: "samples", icon: ClipboardList, title: "Create your first Sample Order", description: "Open the request form and build a new sample order.", onClick: handleCreateSampleOrder, tone: "purple" });
  }
  if (isAdminUser && visibleTerritories.length === 0) {
    quickStartActions.push({ id: "territories", icon: MapPinned, title: "Create your first Territory", description: "Open the map and define your first territory.", onClick: handleNavigateToTerritories, tone: "purple" });
  }
  if (isAdminUser && teamMemberCount <= 1) {
    quickStartActions.push({ id: "team", icon: Users, title: "Invite your Team", description: "Open team settings and bring everyone in.", onClick: handleNavigateToTeam, tone: "slate" });
  }
  if (isAdminUser && !hasLogo) {
    quickStartActions.push({ id: "logo", icon: ImagePlus, title: "Upload Company Logo", description: "Head to profile and update your company branding.", onClick: handleNavigateToProfile, tone: "blue" });
  }

  const convertedRate = useMemo(() => {
    if (!visibleSampleOrders.length) return 0;
    return convertedVisibleSamples.length / visibleSampleOrders.length;
  }, [convertedVisibleSamples.length, visibleSampleOrders.length]);

  const unassignedAccountsCount = useMemo(
    () => (visibleAccounts || []).filter((account) => !getAssignedInternalUserId(account)).length,
    [visibleAccounts]
  );

  const unassignedSampleOrdersCount = useMemo(
    () => (visibleSampleOrders || []).filter((order) => !getAssignedInternalUserId(order)).length,
    [visibleSampleOrders]
  );

  const unassignedAccountsRate = useMemo(() => {
    if (!visibleAccounts.length) return 0;
    return unassignedAccountsCount / visibleAccounts.length;
  }, [unassignedAccountsCount, visibleAccounts.length]);

  const unassignedSampleOrdersRate = useMemo(() => {
    if (!visibleSampleOrders.length) return 0;
    return unassignedSampleOrdersCount / visibleSampleOrders.length;
  }, [unassignedSampleOrdersCount, visibleSampleOrders.length]);

  const accountListRows = useMemo(() => {
    const counts = new Map();

    (visibleSampleOrders || []).forEach((order) => {
      const accountId = order?.accountId ? String(order.accountId) : null;
      if (!accountId) return;

      const current = counts.get(accountId) || { total: 0, active: 0, converted: 0, pending: 0 };
      current.total += 1;
      if (isActiveSampleOrder(order)) current.active += 1;
      if (isConvertedOrder(order)) current.converted += 1;
      if (normalizeStatus(order?.status) === "pending") current.pending += 1;
      counts.set(accountId, current);
    });

    return (visibleAccounts || [])
      .map((account) => {
        const sampleCounts = counts.get(String(account.id)) || { total: 0, active: 0, converted: 0, pending: 0 };
        return {
          account,
          totalSamples: sampleCounts.total,
          activeSamples: sampleCounts.active,
          pendingSamples: sampleCounts.pending,
          convertedSamples: sampleCounts.converted,
          conversionRate: sampleCounts.total > 0 ? sampleCounts.converted / sampleCounts.total : 0,
        };
      })
      .filter((row) => row.totalSamples > 0)
      .sort((a, b) => {
        if (b.totalSamples !== a.totalSamples) return b.totalSamples - a.totalSamples;
        if (b.conversionRate !== a.conversionRate) return b.conversionRate - a.conversionRate;
        return getAccountName(a.account).localeCompare(getAccountName(b.account));
      })
      .slice(0, 6);
  }, [visibleAccounts, visibleSampleOrders]);

  const hasAccountsSection = accountListRows.length > 0;
  const hasRecentSampleOrdersSection = recentOrderHighlights.length > 0;
  const hasInventorySpotlightSection =
    quickInventoryLinks.length > 0 || mostRequested.length > 0;
  const hasCoverageSection = isAdminUser
    ? convertedVisibleSamples.length > 0 ||
      lowStockVisibleInventory.length > 0 ||
      pendingVisibleSamples.length > 0 ||
      visibleTerritories.length > 0 ||
      groups.length > 0
    : visibleAssignedAccounts.length > 0 ||
      pendingVisibleSamples.length > 0 ||
      myGroupNames.length > 0 ||
      myTerritoryNames.length > 0;
  const hasCostTimeFrameSection = companyId && isAdminUser && visibleSampleOrders.length > 0;
  const hasTasksSection = companyId && taskCount > 0;
  const hasActivitySection =
    companyId &&
    isAdminUser &&
    (visibleSampleOrders.length > 0 ||
      visibleAccounts.length > 0 ||
      inventory.length > 0 ||
      taskCount > 0 ||
      teamMemberCount > 1);
  const hasMainDashboardContent =
    hasAccountsSection ||
    hasRecentSampleOrdersSection ||
    hasInventorySpotlightSection ||
    hasCoverageSection ||
    hasCostTimeFrameSection;
  const shouldExpandQuickStart =
    roleLower === "owner" &&
    quickStartActions.length > 0 &&
    !hasMainDashboardContent &&
    !hasTasksSection &&
    !hasActivitySection;

  // ----------------------------
  // Render
  // ----------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <SurfaceState
            tone="loading"
            title="Loading dashboard"
            description="Pulling in your accounts, sample activity, tasks, and operational highlights."
          />
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 max-w-md w-full">
          <div className="text-lg font-semibold text-gray-900">
            No active session
          </div>
          <div className="text-sm text-gray-600 mt-1">
            Please sign in again to view your dashboard.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen -mx-3 md:-mx-5 -mt-2 -mb-4 bg-[radial-gradient(circle_at_top_left,_rgba(96,165,250,0.14),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(16,185,129,0.10),_transparent_30%),linear-gradient(180deg,_#f8fafc_0%,_#eef4ff_100%)]">
      <div className="space-y-3 px-4 py-4 md:px-5 md:py-5">
        <div className="bg-white/92 backdrop-blur rounded-[28px] shadow-sm p-4 md:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-3 min-w-0">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-sm flex-shrink-0">
                  <LayoutDashboard className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl md:text-2xl font-bold text-slate-900">
                      {isAdminUser ? "Operations Dashboard" : "My Dashboard"}
                    </h1>
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {isAdminUser ? "Owner / Admin view" : "User view"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    Welcome back, {userProfile?.fullName || userProfile?.name || userProfile?.email || "User"}.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {isAdminUser
                      ? "See the company-wide pulse, jump into accounts, and move quickly between orders, inventory, and coverage."
                      : "See only the accounts, territories, and sample work that belong to your lane, with quick links into what matters most."}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <StatPill
                  icon={Building2}
                  label={isAdminUser ? "Accounts" : "My Accounts"}
                  value={visibleAccounts.length}
                  color="purple"
                />
                <StatPill
                  icon={ClipboardList}
                  label={isAdminUser ? "Sample Orders" : "My Samples"}
                  value={visibleSampleOrders.length}
                  color="blue"
                />
                <StatPill
                  icon={Package}
                  label={isAdminUser ? "Inventory" : "Visible Inventory"}
                  value={inventory.length}
                  color="gray"
                />
                <StatPill
                  icon={MapPinned}
                  label={isAdminUser ? "Territories" : "Coverage"}
                  value={visibleTerritories.length}
                  color="purple"
                />
                <StatPill
                  icon={Boxes}
                  label="Low Stock"
                  value={lowStockVisibleInventory.length}
                  color="gray"
                />
                <StatPill
                  icon={BadgePercent}
                  label="Converted"
                  value={`${convertedVisibleSamples.length} • ${formatPct(convertedRate)}`}
                  color="blue"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 self-start">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={reloading}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                  reloading
                    ? "bg-gray-100 text-gray-400 border-gray-200"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                <RefreshCcw className={`w-4 h-4 ${reloading ? "animate-spin" : ""}`} />
                {reloading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <SurfaceState
            tone="error"
            compact
            title="Dashboard data issue"
            description={error}
          />
        ) : null}

        <div className="space-y-3 xl:hidden">
          <DashboardCard
            title={isAdminUser ? "Operations Pulse" : "My Coverage"}
            subtitle={
              isAdminUser
                ? "A slimmer operational snapshot for mobile."
                : "Your rep lane, follow-up load, and coverage at a glance."
            }
          >
            {isAdminUser ? (
              <div className="grid grid-cols-2 gap-2">
                <MobileMiniMetric
                  icon={BadgePercent}
                  label="Converted"
                  value={formatPct(convertedRate)}
                  detail={`${convertedVisibleSamples.length} converted`}
                  tone="green"
                />
                <MobileMiniMetric
                  icon={Boxes}
                  label="Low Stock"
                  value={lowStockVisibleInventory.length}
                  detail="Inventory pressure"
                  tone="amber"
                  onClick={() => handleNavigateToInventory()}
                />
                <MobileMiniMetric
                  icon={UserCheck}
                  label="Unassigned Accts"
                  value={formatPct(unassignedAccountsRate)}
                  detail={`${unassignedAccountsCount} open`}
                  tone="purple"
                  onClick={handleNavigateToAccounts}
                />
                <MobileMiniMetric
                  icon={ClipboardList}
                  label="No Rep Samples"
                  value={formatPct(unassignedSampleOrdersRate)}
                  detail={`${unassignedSampleOrdersCount} open`}
                  tone="blue"
                  onClick={() => handleNavigateToSampleOrders()}
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <MobileMiniMetric
                  icon={Building2}
                  label="My Accounts"
                  value={visibleAssignedAccounts.length}
                  detail="Direct ownership"
                  tone="blue"
                  onClick={handleNavigateToAccounts}
                />
                <MobileMiniMetric
                  icon={Clock3}
                  label="Follow Up"
                  value={followUpVisibleSamples.length}
                  detail="Needs attention"
                  tone="amber"
                  onClick={() => handleNavigateToSampleOrders()}
                />
                <MobileMiniMetric
                  icon={MapPinned}
                  label="Territories"
                  value={visibleTerritories.length}
                  detail={myTerritoryNames.length ? myTerritoryNames.slice(0, 2).join(", ") : "No territory assignments"}
                  tone="purple"
                  onClick={handleNavigateToTerritories}
                />
                <MobileMiniMetric
                  icon={Users}
                  label="Groups"
                  value={myGroupNames.length}
                  detail={myGroupNames.length ? myGroupNames.slice(0, 2).join(", ") : "No groups yet"}
                  tone="slate"
                  onClick={handleNavigateToTeam}
                />
              </div>
            )}
          </DashboardCard>

          <DashboardCard
            title={isAdminUser ? "Pending Sample Orders" : "Follow Up Sample Orders"}
            subtitle={
              isAdminUser
                ? "Requests waiting on approval or review."
                : "Your follow-up work, expanded by default."
            }
            right={
              <ActionLink
                label="Open Samples"
                icon={ArrowRight}
                onClick={() => handleNavigateToSampleOrders()}
              />
            }
          >
            {mobileSampleQueue.length === 0 ? (
              <EmptyState>
                {isAdminUser
                  ? "No pending sample orders right now."
                  : "No sample orders in Follow Up right now."}
              </EmptyState>
            ) : (
              <div className="space-y-2">
                {mobileSampleQueue.map((order) => (
                  <MobileActionRow
                    key={order.id}
                    title={order.accountName}
                    subtitle={`${order.status || (isAdminUser ? "Pending" : "Follow Up")} • ${order.detail}`}
                    badge={order.requestedDate || order.deliveryDate || ""}
                    onClick={() => handleNavigateToSampleOrders(order.id)}
                    actionLabel={isAdminUser && normalizeStatus(order?.status) === "pending" ? "Review" : "Open"}
                  />
                ))}
              </div>
            )}
          </DashboardCard>

          {hasTasksSection ? (
            <TasksSummary
              companyId={companyId}
              me={{
                ...(userProfile || {}),
                id: userProfile?.id || firebaseUser?.uid || null,
                uid: firebaseUser?.uid || userProfile?.uid || null,
              }}
            />
          ) : null}

          <DashboardCard
            title="Inventory Spotlight"
            subtitle="Quick inventory links in a tighter mobile format."
            right={
              <ActionLink
                label="Inventory"
                icon={ArrowRight}
                onClick={() => handleNavigateToInventory()}
              />
            }
          >
            {mobileInventorySpotlight.length === 0 ? (
              <EmptyState>No inventory highlights yet.</EmptyState>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {mobileInventorySpotlight.map((row) => (
                  <MobileMiniMetric
                    key={row.id}
                    icon={Package}
                    label={row.badge || "Item"}
                    value={row.title}
                    detail={row.detail}
                    tone={row.tone}
                    onClick={() => handleNavigateToInventory(row.id)}
                  />
                ))}
              </div>
            )}
          </DashboardCard>
        </div>

        <div className="hidden xl:grid xl:grid-cols-12 gap-3">
          <div className={shouldExpandQuickStart ? "hidden" : "xl:col-start-1 xl:col-span-8 space-y-3"}>
            {hasAccountsSection || hasRecentSampleOrdersSection ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {hasAccountsSection ? (
              <DashboardCard
                title="Accounts"
                subtitle="Only accounts with sample history stay here, with quick conversion context instead of a long master list."
              >
                <div className="space-y-1.5">
                  {accountListRows.map((row) => (
                    <button
                      key={row.account.id}
                      type="button"
                      onClick={() => handleNavigateToAccount(row.account.id)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-900">{getAccountName(row.account)}</div>
                          <div className="mt-0.5 truncate text-[11px] text-slate-500">
                            {row.account?.assignedToName || row.account?.contactName || "Sample activity on file"}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 font-semibold text-slate-700">
                            {row.totalSamples} orders
                          </span>
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 font-semibold text-emerald-700">
                            {formatPct(row.conversionRate)} conv
                          </span>
                          {row.pendingSamples > 0 ? (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 font-semibold text-amber-700">
                              {row.pendingSamples} pending
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </DashboardCard>
              ) : null}

              {hasRecentSampleOrdersSection ? (
              <DashboardCard
                title="Recent Sample Orders"
                subtitle="Latest requests you can act on right away"
                right={
                  <ActionLink
                    label="New Order"
                    icon={PlusCircle}
                    onClick={handleCreateSampleOrder}
                    primary
                  />
                }
              >
                <div className="space-y-2">
                  {recentOrderHighlights.map((order) => {
                    const canQuickApprove =
                      isAdminUser && normalizeStatus(order?.status) === "pending";

                    return (
                      <div
                        key={order.id}
                        className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2"
                      >
                        <button
                          type="button"
                          onClick={() => handleNavigateToSampleOrders(order.id)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="truncate text-sm font-semibold text-slate-900">{order.accountName}</div>
                          <div className="mt-0.5 truncate text-xs text-slate-500">
                            {`${order?.status || "Pending"} • ${order.detail}`}
                          </div>
                        </button>
                        {canQuickApprove ? (
                          <button
                            type="button"
                            onClick={() => handleQuickApprove(order.id)}
                            disabled={approvingOrderId === order.id}
                            className="shrink-0 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                          >
                            {approvingOrderId === order.id ? "Approving…" : "Quick Approve"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleNavigateToSampleOrders(order.id)}
                            className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            Open
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </DashboardCard>
              ) : null}
            </div>
            ) : null}

            {hasInventorySpotlightSection || hasCoverageSection ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {hasInventorySpotlightSection ? (
              <DashboardCard
                title="Inventory Spotlight"
                subtitle="Quick-linked inventory so you can move straight from the dashboard into the right item"
                right={
                  <ActionLink
                    label="Open Inventory"
                    icon={ArrowRight}
                    onClick={() => handleNavigateToInventory()}
                  />
                }
              >
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <Sparkles className="w-3.5 h-3.5 text-yellow-500" />
                      Quick Links
                    </div>
                    <div className="space-y-2">
                      {quickInventoryLinks.slice(0, 2).map(({ item, subtitle, badge, accent }) => (
                        <QuickRow
                          key={`featured_${item.id}`}
                          title={getInventoryTitle(item)}
                          subtitle={subtitle}
                          badge={badge}
                          accent={accent}
                          onClick={() => handleNavigateToInventory(item.id)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      <Flame className="w-3.5 h-3.5 text-red-500" />
                      Most Requested
                    </div>
                    {mostRequested.length === 0 ? (
                      <EmptyState>No request data yet.</EmptyState>
                    ) : (
                      <div className="space-y-2">
                        {mostRequested.slice(0, 3).map(({ item, requests }) => (
                          <QuickRow
                            key={`hot_${item.id}`}
                            title={getInventoryTitle(item)}
                            subtitle={item?.size ? `${item.size} ${item?.unit || ""}` : "Inventory item"}
                            badge={`${requests} req`}
                            accent="green"
                            onClick={() => handleNavigateToInventory(item.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </DashboardCard>
              ) : null}

              {hasCoverageSection ? (
              <DashboardCard
                title={isAdminUser ? "Operations Pulse" : "My Coverage"}
                subtitle={
                  isAdminUser
                    ? "A compact snapshot of revenue motion, stock pressure, and team-facing momentum."
                    : "A cleaner summary of your rep, groups, and the territory context driving your work."
                }
              >
                {isAdminUser ? (
                  <div className="grid grid-cols-1 gap-2">
                    <div className="grid grid-cols-2 gap-2">
                      <MetricTile
                        icon={DollarSign}
                        label="Conversion Rate"
                        value={formatPct(convertedRate)}
                        detail={`${convertedVisibleSamples.length} converted`}
                        tone="green"
                      />
                      <MetricTile
                        icon={Boxes}
                        label="Inventory Pressure"
                        value={lowStockVisibleInventory.length}
                        detail="Low-stock items"
                        tone="amber"
                        onClick={() => handleNavigateToInventory()}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <MetricTile
                        icon={UserCheck}
                        label="Accounts Without Owner"
                        value={formatPct(unassignedAccountsRate)}
                        detail={`${unassignedAccountsCount} unassigned`}
                        tone="purple"
                        onClick={handleNavigateToAccounts}
                      />
                      <MetricTile
                        icon={ClipboardList}
                        label="Samples Without Rep"
                        value={formatPct(unassignedSampleOrdersRate)}
                        detail={`${unassignedSampleOrdersCount} unassigned`}
                        tone="blue"
                        onClick={() => handleNavigateToSampleOrders()}
                      />
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                        Attention lane
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        {pendingVisibleSamples.length > 0
                          ? `${pendingVisibleSamples.length} sample orders are still pending across the current company view.`
                          : "No pending sample orders are waiting right now."}
                      </div>
                    </div>
                    {(visibleTerritories.length > 0 || groups.length > 0) ? (
                      <div className="grid grid-cols-2 gap-2">
                        {visibleTerritories.length > 0 ? (
                          <MetricTile
                            icon={MapPinned}
                            label="Territories"
                            value={visibleTerritories.length}
                            detail="Coverage map is active"
                            tone="purple"
                            onClick={handleNavigateToTerritories}
                          />
                        ) : null}
                        {groups.length > 0 ? (
                          <MetricTile
                            icon={Users}
                            label="Groups"
                            value={groups.length}
                            detail="Team visibility groups"
                            tone="blue"
                            onClick={handleNavigateToTeam}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <MetricTile
                        icon={UserCheck}
                        label="Assigned Accounts"
                        value={visibleAssignedAccounts.length}
                        detail="Direct ownership"
                        tone="blue"
                        onClick={handleNavigateToAccounts}
                      />
                      <MetricTile
                        icon={Clock3}
                        label="Pending Samples"
                        value={pendingVisibleSamples.length}
                        detail="Needs follow-through"
                        tone="amber"
                        onClick={() => handleNavigateToSampleOrders()}
                      />
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
                        Groups
                      </div>
                      {myGroupNames.length === 0 ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                          No group assignments yet.
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {myGroupNames.map((groupName) => (
                            <span
                              key={groupName}
                              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700"
                            >
                              {groupName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
                        Territories
                      </div>
                      {myTerritoryNames.length === 0 ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                          No territory assignments yet.
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {myTerritoryNames.map((territoryName) => (
                            <span
                              key={territoryName}
                              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700"
                            >
                              {territoryName}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </DashboardCard>
              ) : null}
            </div>
            ) : null}

            {hasCostTimeFrameSection ? (
              <SampleOrderCostTimeFrame
                companyId={companyId}
                onCreateSampleOrder={handleCreateSampleOrder}
              />
            ) : null}
          </div>

          <div
            className={
              shouldExpandQuickStart
                ? "xl:col-start-1 xl:col-span-12 space-y-3"
                : "xl:col-start-9 xl:col-span-4 space-y-3"
            }
          >
            {roleLower === "owner" && quickStartActions.length > 0 ? (
              <div className="-mx-4 md:-mx-5 bg-gradient-to-r from-slate-50 via-blue-50 to-emerald-50 px-4 py-4 md:px-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Quick Start</div>
                    <div className="mt-1 text-sm font-semibold text-slate-900">Set up the essentials</div>
                    <div className="mt-1 text-xs text-slate-500">Only the missing setup steps stay here, and each one opens the right screen.</div>
                  </div>
                </div>

                <div
                  className={`mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 ${
                    shouldExpandQuickStart ? "xl:grid-cols-3" : "xl:grid-cols-1"
                  }`}
                >
                  {quickStartActions.map((action) => (
                    <QuickStartTile
                      key={action.id}
                      icon={action.icon}
                      title={action.title}
                      description={action.description}
                      onClick={action.onClick}
                      tone={action.tone}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {hasTasksSection ? (
              <TasksSummary
                companyId={companyId}
                me={{
                  ...(userProfile || {}),
                  id: userProfile?.id || firebaseUser?.uid || null,
                  uid: firebaseUser?.uid || userProfile?.uid || null,
                }}
              />
            ) : null}

            {hasActivitySection ? (
              <ActivityLog
                companyId={companyId}
                title="Recent Activity"
                showControls={true}
                maxHeight="320px"
                pageSize={3}
              />
            ) : null}

            {!hasMainDashboardContent && !hasTasksSection && !hasActivitySection ? (
              <SurfaceState
                tone="empty"
                title="Your dashboard is ready for first activity"
                description="Once you add accounts, inventory, sample orders, or tasks, this page will start turning into a live operations view."
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* ✅ Route builder modal */}
      <RouteBuilderModal
        open={showRouteModal}
        onClose={() => setShowRouteModal(false)}
        companyId={companyId}
        me={userProfile}
        accounts={visibleAccounts}
        sampleOrders={visibleSampleOrders}
        territories={visibleTerritories}
      />
    </div>
  );
}
